import { useState } from 'react'

import { TasksService } from '@/services/tasks.service'

import {
	IGeneratedHighlightResult,
	IHighlightsFolder,
	ISegment
} from '@/types/highlight.interface'
import { IFile } from '@/types/task.interface'
import { displayTime } from '@/utils/displayTime'
import { formatTime } from '@/utils/formatTime'
import { toastError } from '@/utils/toastError'

const statuses = {
	PENDING: 'The task has been added to the queue',
	STARTED: 'Highlight is being created',
	RETRY: 'Highlight is being created',
	UNKNOWN: 'Highlight generation timed out, retrying'
}

export const useGenerate = ({
	taskId,
	uploadHighlight
}: {
	taskId?: string
	uploadHighlight: (data: {
		id: string
		range: { start: number; end: number }[]
	}) => Promise<{ job_id: string; task_id: string }>
}) => {
	const [generatedResults, setGeneratedResults] = useState<
		IGeneratedHighlightResult[]
	>([])
	const [isGeneratedResultsOpen, setIsGeneratedResultsOpen] = useState(false)
	const [isJobLoading, setIsJobLoading] = useState(false)
	const [jobStatus, setJobStatus] = useState<null | string>(null)

	const closeGeneratedResults = () => {
		setIsGeneratedResultsOpen(false)
		setGeneratedResults([])
	}

	const pollHighlightJob = async (
		taskId: string,
		jobId: string,
		retried = false
	): Promise<IFile[]> => {
		return new Promise(resolve => {
			const pollJob = async () => {
				try {
					const res = await TasksService.getHighlightJobStatus(taskId, jobId)

					setJobStatus(res.status)

					if (res.status === 'SUCCESS') {
						resolve(
							(res.files || res.result?.files || []) as unknown as IFile[]
						)
						return
					}

					if (res.status === 'FAILURE') {
						toastError(
							'Failed to generate highlight. Please contact technical support.'
						)
						console.log('Highlight generation failed:', res.error)
						resolve([])
						return
					}

					// UNKNOWN: the backend saw no result within its timeout.
					// Regenerate once automatically; give up on a second timeout.
					if (res.status === 'UNKNOWN') {
						if (retried) {
							toastError('Highlight generation timed out. Please try again.')
							console.log('Highlight generation timed out:', res.error)
							resolve([])
							return
						}

						const retry = await TasksService.retryHighlightJob(taskId, jobId)
						resolve(await pollHighlightJob(taskId, retry.job_id, true))
						return
					}

					setTimeout(pollJob, 3000)
				} catch (error) {
					console.log('Error polling highlight status:', error)
					toastError(
						'Failed to check highlight generation status. Please try again.',
						{
							toastId: 'highlight-status-error'
						}
					)
					resolve([])
				}
			}

			pollJob()
		})
	}

	const generateHighlightFiles = async (
		ranges: { start: number; end: number }[]
	): Promise<IFile[]> => {
		if (!taskId || !ranges.length) return []

		const { job_id, task_id } = await uploadHighlight({
			id: taskId,
			range: ranges
		})

		if (!job_id) {
			console.log('No job_id received from backend')
			return []
		}

		return pollHighlightJob(task_id, job_id)
	}

	const buildFolderGenerationResult = (
		folder: IHighlightsFolder,
		files: IFile[]
	): IGeneratedHighlightResult => {
		let totalSeconds = 0

		folder.segments.forEach(segment => {
			totalSeconds += segment.end - segment.start
		})

		return {
			id: folder.id,
			name: folder.name,
			colorCode: folder.color_code,
			segmentsCount: folder.segments.length,
			totalTime: formatTime(totalSeconds),
			files
		}
	}

	const downloadSegment = async ({
		segment,
		folder
	}: {
		segment: ISegment
		folder: IHighlightsFolder
	}) => {
		setIsJobLoading(true)
		setJobStatus('PENDING')

		try {
			const files = await generateHighlightFiles([
				{ start: segment.start, end: segment.end }
			])

			setGeneratedResults([
				{
					id: segment.id || `${segment.start}-${segment.end}`,
					name:
						`${displayTime(segment.start)}-${displayTime(segment.end)}` ||
						'Selected segment',
					colorCode: folder.color_code,
					segmentsCount: 1,
					totalTime: formatTime(segment.end - segment.start),
					files
				}
			])

			setIsGeneratedResultsOpen(true)
		} catch (error) {
			console.log(error)
			toastError('Failed to generate highlight. Please try again.')
		} finally {
			setIsJobLoading(false)
			setJobStatus(null)
		}
	}

	const downloadFolder = async (folder: IHighlightsFolder) => {
		if (!folder.segments.length) return

		setIsJobLoading(true)
		setJobStatus('PENDING')

		try {
			const files = await generateHighlightFiles(
				folder.segments.map(({ start, end }) => ({ start, end }))
			)

			setGeneratedResults([buildFolderGenerationResult(folder, files)])
			setIsGeneratedResultsOpen(true)
		} catch (error) {
			console.log(error)
			toastError('Failed to generate folder. Please try again.')
		} finally {
			setIsJobLoading(false)
			setJobStatus(null)
		}
	}

	const downloadSelectedFolders = async ({
		folders,
		selectedFolderIds
	}: {
		folders: IHighlightsFolder[]
		selectedFolderIds: number[]
	}) => {
		const selectedFolders = folders.filter(folder =>
			selectedFolderIds.includes(folder.id)
		)

		if (!selectedFolders.length) return

		setIsJobLoading(true)
		setJobStatus('PENDING')

		try {
			const results = await Promise.all(
				selectedFolders.map(async folder => {
					const files = await generateHighlightFiles(
						folder.segments.map(({ start, end }) => ({ start, end }))
					)

					return buildFolderGenerationResult(folder, files)
				})
			)

			setGeneratedResults(results)
			setIsGeneratedResultsOpen(true)
		} catch (error) {
			console.log(error)
			toastError('Failed to generate selected folders. Please try again.')
		} finally {
			setIsJobLoading(false)
			setJobStatus(null)
		}
	}

	return {
		generatedResults,
		isGeneratedResultsOpen,
		closeGeneratedResults,
		isJobLoading,
		jobStatus,
		statusMessage: jobStatus
			? statuses[jobStatus as keyof typeof statuses]
			: null,
		downloadSegment,
		downloadFolder,
		downloadSelectedFolders
	}
}
